import {
	Building2,
	FolderTree,
	LayoutDashboard,
	LogOut,
	MapPin,
	Package,
	Settings,
	Users,
} from "lucide-react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupContent,
	SidebarGroupLabel,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarRail,
} from "@/components/ui/sidebar";
import { useAuthStore } from "@/stores";

export function AppSidebar() {
	const { t } = useTranslation();
	const location = useLocation();
	const { user, logout } = useAuthStore();

	// Memoize navigation items to prevent recreation on every render
	const mainNavItems = useMemo(
		() => [
			{
				title: t("nav.dashboard"),
				url: "/dashboard",
				icon: LayoutDashboard,
			},
			{
				title: t("nav.assets"),
				url: "/assets",
				icon: Package,
			},
			{
				title: t("nav.assetTypes"),
				url: "/asset-types",
				icon: FolderTree,
			},
			{
				title: t("nav.locations"),
				url: "/locations",
				icon: MapPin,
			},
		],
		[t],
	);

	// Memoize admin navigation items
	const adminNavItems = useMemo(
		() => [
			{
				title: t("nav.users"),
				url: "/users",
				icon: Users,
			},
			{
				title: t("nav.settings"),
				url: "/settings",
				icon: Settings,
			},
		],
		[t],
	);

	// Memoize isActive callback for consistent reference
	const isActive = useCallback(
		(url: string) => {
			if (url === "/dashboard") {
				return location.pathname === url;
			}
			return location.pathname.startsWith(url);
		},
		[location.pathname],
	);

	// Memoize user initials calculation (e.g., "Maria Fernanda Gomez" -> "MG")
	const userInitials = useMemo(() => {
		if (!user?.name) {
			return "??";
		}
		return user.name
			.split(" ")
			.filter((_, i, arr) => i === 0 || i === arr.length - 1)
			.map((n) => n[0])
			.join("")
			.toUpperCase();
	}, [user?.name]);

	// Memoize admin check
	const isAdmin = useMemo(() => user?.role === "admin", [user?.role]);

	return (
		<Sidebar collapsible="icon" data-testid="sidebar">
			<SidebarHeader>
				<SidebarMenu>
					<SidebarMenuItem>
						<SidebarMenuButton size="lg" asChild>
							<Link to="/dashboard" data-testid="sidebar-logo">
								<div
									className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground"
									aria-hidden="true"
								>
									<Building2 className="size-4" />
								</div>
								<div className="grid flex-1 text-left text-sm leading-tight">
									<span className="truncate font-semibold">
										IPS Activos
										<span className="sr-only">- Ir al panel de control</span>
									</span>
									<span className="truncate text-xs text-muted-foreground">
										Gestión de Inventario
									</span>
								</div>
							</Link>
						</SidebarMenuButton>
					</SidebarMenuItem>
				</SidebarMenu>
			</SidebarHeader>

			<SidebarContent>
				<SidebarGroup>
					<SidebarGroupLabel>{t("nav.main")}</SidebarGroupLabel>
					<SidebarGroupContent>
						<SidebarMenu data-testid="sidebar-nav">
							{mainNavItems.map((item) => {
								const urlPath = item.url.replace("/", "");
								const testId = `sidebar-nav-${urlPath}`;
								return (
									<SidebarMenuItem key={item.title}>
										<SidebarMenuButton asChild isActive={isActive(item.url)} tooltip={item.title}>
											<Link to={item.url} data-testid={testId}>
												<item.icon aria-hidden="true" />
												<span>{item.title}</span>
											</Link>
										</SidebarMenuButton>
									</SidebarMenuItem>
								);
							})}
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>

				{isAdmin && (
					<SidebarGroup>
						<SidebarGroupLabel>{t("nav.admin")}</SidebarGroupLabel>
						<SidebarGroupContent>
							<SidebarMenu>
								{adminNavItems.map((item) => {
									const urlPath = item.url.replace("/", "");
									const testId = `sidebar-nav-${urlPath}`;
									return (
										<SidebarMenuItem key={item.title}>
											<SidebarMenuButton asChild isActive={isActive(item.url)} tooltip={item.title}>
												<Link to={item.url} data-testid={testId}>
													<item.icon aria-hidden="true" />
													<span>{item.title}</span>
												</Link>
											</SidebarMenuButton>
										</SidebarMenuItem>
									);
								})}
							</SidebarMenu>
						</SidebarGroupContent>
					</SidebarGroup>
				)}
			</SidebarContent>

			<SidebarFooter>
				<SidebarMenu>
					<SidebarMenuItem>
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<SidebarMenuButton
									size="lg"
									className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
									aria-label={`Menú de usuario para ${user?.name ?? "usuario"}`}
									data-testid="sidebar-user-menu"
								>
									<Avatar className="size-8" aria-label={`Avatar de ${user?.name ?? "usuario"}`}>
										<AvatarFallback className="text-xs">{userInitials}</AvatarFallback>
									</Avatar>
									<div className="grid flex-1 text-left text-sm leading-tight">
										<span className="truncate font-semibold">{user?.name}</span>
										<span className="truncate text-xs text-muted-foreground">{user?.email}</span>
									</div>
								</SidebarMenuButton>
							</DropdownMenuTrigger>
							<DropdownMenuContent
								className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
								side="bottom"
								align="end"
								sideOffset={4}
								data-testid="sidebar-user-dropdown"
							>
								<DropdownMenuItem asChild>
									<Link to="/settings">
										<Settings className="mr-2 size-4" />
										{t("nav.settings")}
									</Link>
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem onClick={logout} data-testid="sidebar-logout-button">
									<LogOut className="mr-2 size-4" />
									{t("nav.logout")}
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					</SidebarMenuItem>
				</SidebarMenu>
			</SidebarFooter>

			<SidebarRail />
		</Sidebar>
	);
}
